import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LoginActions,
  QueryParameterNames,
  ApplicationPaths
} from './ApiAuthorizationConstants';
import { Spinner } from '../Utils/Loaders';
import { useAuth } from '../../auth/AuthContext';

export interface IProps {
  action: string;
}

export const Login = ({ action }: IProps): JSX.Element => {
  const [message, setMessage] = useState<string | null>(null);
  const { signinRedirect, completeSignIn } = useAuth();
  const navigate = useNavigate();

  const getReturnUrl = (returnUrl?: string) => {
    const params = new URLSearchParams(globalThis.location.search);
    const fromQuery = params.get(QueryParameterNames.ReturnUrl);
    if (fromQuery && !fromQuery.startsWith(`${globalThis.location.origin}/`)) {
      throw new Error(
        'Invalid return url. The return url needs to have the same origin as the current page.'
      );
    }
    return returnUrl || fromQuery || `${globalThis.location.origin}/`;
  };

  const toAppPath = (url: string): string => {
    try {
      const parsed = new URL(url, globalThis.location.origin);
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return '/';
    }
  };

  useEffect(() => {
    const run = async () => {
      if (action === LoginActions.Login) {
        await signinRedirect(getReturnUrl());
        return;
      }

      if (action === LoginActions.LoginCallback) {
        try {
          const returnUrl = await completeSignIn(globalThis.location.href);
          const target = returnUrl || getReturnUrl();
          navigate(toAppPath(target), { replace: true });
        } catch (err) {
          const error = err as Error;
          setMessage(error?.message ?? 'Login failed');
        }
        return;
      }

      if (action === LoginActions.LoginFailed) {
        const params = new URLSearchParams(globalThis.location.search);
        setMessage(params.get(QueryParameterNames.Message));
        return;
      }

      if (action === LoginActions.Profile) {
        globalThis.location.replace(`${globalThis.location.origin}${ApplicationPaths.Profile}`);
        return;
      }

      if (action === LoginActions.Register) {
        const registerUrl = `${ApplicationPaths.IdentityRegisterPath}?${QueryParameterNames.ReturnUrl}=${encodeURIComponent(ApplicationPaths.Login)}`;
        globalThis.location.replace(`${globalThis.location.origin}${registerUrl}`);
        return;
      }

      throw new Error(`Invalid action '${action}'`);
    };

    run();
  }, [action, completeSignIn, signinRedirect, navigate]);

  if (message) {
    return <div>{message}</div>;
  }

  return (
    <div>
      <Spinner />
    </div>
  );
};
